import { FitBar, FitLine, GroupLabel, KeywordChip, KeywordChips } from "@/components/posting/parts";
import type { PostingKeyword } from "@/lib/api/catalogue";
import type { ToolFit } from "@/lib/tools/check";

const WELL = "flex flex-col gap-2 rounded-control bg-card-raised p-3.5";

/** The count, then what the resume names and what it does not. No percent, ever. */
export default function CheckResult({ fit }: { fit: ToolFit }) {
    return (
        <>
            <div className="flex flex-col gap-2">
                <FitLine covered={fit.covered} asked={fit.asked}>
                    keywords your resume says
                </FitLine>
                <FitBar share={fit.share} />
            </div>
            <div data-testid="covered-keywords" className={WELL}>
                <GroupLabel>On your resume</GroupLabel>
                {fit.covered > 0 ? (
                    <KeywordChips terms={fit.coveredTerms} kind="covered" />
                ) : (
                    <span className="text-ui text-fg-muted">None.</span>
                )}
            </div>
            <div data-testid="missing-keywords" className={WELL}>
                <GroupLabel>Not on your resume</GroupLabel>
                {fit.missing.length > 0 ? (
                    <>
                        <KeywordChips terms={fit.missing} kind="missing" />
                        <span className="text-small text-fg-muted [text-wrap:pretty]">
                            A tailor cannot add these. Decide whether they are true of you before
                            you apply.
                        </span>
                    </>
                ) : (
                    <span className="text-ui text-fg-muted">
                        Nothing. Your resume names all of them.
                    </span>
                )}
            </div>
        </>
    );
}

/** A posting's keywords, in its order, each turning green as the resume names it. */
export function WaitingKeywords({
    keywords,
    fit,
}: {
    keywords: readonly PostingKeyword[];
    fit: ToolFit;
}) {
    const covered = new Set(fit.coveredTerms);
    return (
        <div data-testid="waiting-keywords" className={WELL}>
            <GroupLabel>{`This job names ${fit.asked} ${fit.asked === 1 ? "keyword" : "keywords"}`}</GroupLabel>
            <div className="flex flex-wrap gap-1.5">
                {keywords.map(({ term }, index) => (
                    <KeywordChip
                        // A posting can ask for the same word twice.
                        key={`${index}:${term}`}
                        term={term}
                        kind={covered.has(term) ? "covered" : "missing"}
                    />
                ))}
            </div>
        </div>
    );
}
